import styled from "@emotion/styled";
import { useCallback } from "react";
import { useSelector } from "react-redux";

import { RootState } from "../../redux/root";
import { analyticsEvent } from "@util/analytics";
import { ViewAsPreservingLink } from "../common/ViewAsPreservingLink";

import { Heading2, RegularText } from "../revamped/Typography";
import ReferralSocialButton from "./ReferralSocialButton";

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px 16px 20px;
  text-align: center;
  border-radius: 4px;
  background: radial-gradient(100% 583.1% at 0% 2.3%, #c7ffe7 0%, #d4edff 100%);

  @media (min-width: 428px) {
    padding: 24px 32px 20px;
  }

  @media (min-width: 559px) {
    flex-direction: row;
    align-items: center;
    padding: 24px 40px 20px 32px;
    text-align: initial;
  }
`;

const BannerText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const BannerButtons = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, 172px);
  gap: 12px;
  justify-content: center;
`;

const StyledBannerText = styled(RegularText)`
  color: #414348;

  a {
    font-weight: 500;
    color: inherit;
    text-decoration: underline;

    &:hover {
      color: #000;
      text-decoration: none;
    }
  }
`;

const StyledBannerHeading = styled(Heading2)`
  font-weight: 600;
  font-size: 18px;
  line-height: 1;

  @media (min-width: 428px) {
    font-size: 24px;
    line-height: 125%;
  }
`;

const socialMediums = ["twitter", "linkedin", "discord", "telegram"] as const;

interface ReferralModalBannerProps {
  onCloseModal: () => void;
}

const ReferralModalBanner = ({ onCloseModal }: ReferralModalBannerProps) => {
  const referralSocialBonuses = useSelector(
    (state: RootState) => state.team.referralSocialBonuses,
  );

  const socialMediumsReceived = socialMediums.filter((medium) =>
    referralSocialBonuses.value?.mediums.includes(medium),
  );

  const handleClickBillingLink = useCallback(() => {
    onCloseModal();

    analyticsEvent("Referral: Clicked billing link in modal");
  }, [onCloseModal]);

  const allReceived = socialMediumsReceived.length === socialMediums.length;
  const noneReceived = socialMediumsReceived.length === 0;
  const billingLink = (
    <ViewAsPreservingLink
      to="/settings/billing"
      onClick={handleClickBillingLink}
    >
      track your credits in billing
    </ViewAsPreservingLink>
  );

  return (
    <Root>
      <BannerText>
        <StyledBannerHeading>
          {noneReceived
            ? "Unlock $40 bonus"
            : allReceived
            ? `You’ve earned $${socialMediums.length * 10}!`
            : `Great, you earned $${socialMediumsReceived.length * 10}`}
        </StyledBannerHeading>

        <StyledBannerText>
          {noneReceived ? (
            <>
              Share alpha with your fellow devs! Refer your friends to Alchemy
              through Twitter, LinkedIn, Discord, and Telegram, and get up to
              $40 credits right away
            </>
          ) : allReceived ? (
            <>
              Keep referring and get $100 credits when your friend signs up for
              growth and enterprise plan. You can {billingLink}.
            </>
          ) : (
            <>
              You can {billingLink}. Keep sharing Alchemy to your friends to get
              the remaining bonus credits.
            </>
          )}
        </StyledBannerText>
      </BannerText>

      <BannerButtons>
        {socialMediums.map((medium) => (
          <ReferralSocialButton key={medium} medium={medium} />
        ))}
      </BannerButtons>
    </Root>
  );
};

export default ReferralModalBanner;
